import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Mail, Lock, User, BookOpen, CheckSquare, X, Loader2, Check, Upload } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { useToast } from "@/hooks/use-toast";

const BLANK_STUDENT = { full_name: "", email: "", password: "" };

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { label: "One uppercase letter (A–Z)", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One lowercase letter (a–z)", test: (p: string) => /[a-z]/.test(p) },
  { label: "One number (0–9)", test: (p: string) => /[0-9]/.test(p) },
  { label: "One special character (!@#$…)", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

const isPasswordValid = (p: string) => PASSWORD_RULES.every(r => r.test(p));

const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTO_MB = 8;      // what we accept from disk
const PHOTO_MAX_EDGE = 600;  // what we send after downscaling

// Downscales to a square-ish JPEG data URL. ThinkExam wants a base64 profilePic,
// and a phone photo straight off disk is far larger than the API needs.
const fileToScaledDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file is not a readable image"));
      img.onload = () => {
        const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Could not process that image")); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });

const RegisterStudent = () => {
  const [creating, setCreating] = useState(false);
  const [newStudent, setNewStudent] = useState(BLANK_STUDENT);
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoName, setPhotoName] = useState("");

  // ThinkExam exam selection state
  const [groups, setGroups] = useState<any[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [exams, setExams] = useState<any[]>([]);
  const [examsLoading, setExamsLoading] = useState(false);
  const [selectedExams, setSelectedExams] = useState<{ testId: string; testName: string }[]>([]);

  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch ThinkExam groups on mount
  useEffect(() => {
    setGroupsLoading(true);
    api.get("/think-exam/groups")
      .then(res => setGroups(res.data?.groups || []))
      .catch(e => toast({ title: "Failed to load groups", description: e.message, variant: "destructive" }))
      .finally(() => setGroupsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch exams when group changes
  useEffect(() => {
    if (!selectedGroup) { setExams([]); return; }
    setExamsLoading(true);
    setSelectedExams([]);
    api.post("/think-exam/exams", { groupName: selectedGroup })
      .then(res => setExams(res.data?.exams || []))
      .catch(e => toast({ title: "Failed to load exams", description: e.message, variant: "destructive" }))
      .finally(() => setExamsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  const toggleExam = (exam: any) => {
    const id = exam.testId || exam.id || exam.test_id;
    const name = exam.testName || exam.name || exam.test_name || id;
    setSelectedExams(prev =>
      prev.some(e => e.testId === id)
        ? prev.filter(e => e.testId !== id)
        : [...prev, { testId: id, testName: name }]
    );
  };

  const handlePhotoPick = async (file: File | undefined) => {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast({ title: "Unsupported file", description: "Please choose a JPG, PNG or WebP image.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      toast({ title: "Image too large", description: `Please choose an image under ${MAX_PHOTO_MB} MB.`, variant: "destructive" });
      return;
    }
    try {
      setPhoto(await fileToScaledDataUrl(file));
      setPhotoName(file.name);
    } catch (e: any) {
      toast({ title: "Could not read image", description: e.message, variant: "destructive" });
    }
  };

  const handleCreateStudent = async () => {
    if (!newStudent.full_name || !newStudent.email || !newStudent.password) {
      toast({ title: "Missing fields", description: "Please fill all student details.", variant: "destructive" });
      return;
    }
    if (newStudent.full_name.includes("@")) {
      toast({ title: "Invalid name", description: "Full name should be the student's real name, not an email address.", variant: "destructive" });
      return;
    }
    if (!isPasswordValid(newStudent.password)) {
      toast({ title: "Weak password", description: "Password must be 8+ characters with an uppercase, lowercase, number, and special character.", variant: "destructive" });
      return;
    }
    if (!photo) {
      toast({ title: "Photo required", description: "Upload the student's photo — ThinkExam needs it to register the candidate.", variant: "destructive" });
      return;
    }
    if (!selectedGroup) {
      toast({ title: "Select a group", description: "Please select a ThinkExam group.", variant: "destructive" });
      return;
    }
    if (selectedExams.length === 0) {
      toast({ title: "Select exams", description: "Please select at least one exam.", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", {
        email: newStudent.email,
        password: newStudent.password,
        full_name: newStudent.full_name,
        role: "student",
        think_exam_group_name: selectedGroup,
        think_exam_exams: selectedExams,
        profile_pic: photo,
      });
      toast({ title: "Student created", description: `${newStudent.full_name} has been registered and exams assigned.` });
      navigate("/growlearn/students");
    } catch (err: any) {
      toast({ title: "Error", description: err.response?.data?.error || err.message, variant: "destructive" });
      setCreating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Register Student" />

        {/* Two-column content */}
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          {/* Student Details */}
          <Card className="glass-premium">
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <User className="h-4 w-4" />
                </span>
                Student Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-5">
              {/* Photo — required: ThinkExam's candidate registration rejects a student without one */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Photo <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 shrink-0 rounded-full border border-border/60 bg-muted/30 overflow-hidden flex items-center justify-center">
                    {photo
                      ? <img src={photo} alt={newStudent.full_name || "The student"} className="h-full w-full object-cover" />
                      : <User className="h-7 w-7 text-muted-foreground/40" />}
                  </div>
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <label>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={e => { handlePhotoPick(e.target.files?.[0]); e.target.value = ""; }}
                        />
                        <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-primary/40 text-primary text-xs font-semibold cursor-pointer hover:bg-primary hover:text-white hover:border-primary transition-all duration-200">
                          <Upload className="h-3.5 w-3.5" />
                          {photo ? "Replace" : "Upload photo"}
                        </span>
                      </label>
                      {photo && (
                        <Button variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-destructive" onClick={() => { setPhoto(null); setPhotoName(""); }}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {photoName || `JPG, PNG or WebP — up to ${MAX_PHOTO_MB} MB`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9 h-11" autoComplete="off" value={newStudent.full_name} onChange={e => setNewStudent({ ...newStudent, full_name: e.target.value })} placeholder="John Doe" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9 h-11" type="email" autoComplete="off" name="student-email-field" value={newStudent.email} onChange={e => setNewStudent({ ...newStudent, email: e.target.value })} placeholder="john@example.com" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9 h-11" type="password" autoComplete="new-password" name="student-password-field" value={newStudent.password} onChange={e => setNewStudent({ ...newStudent, password: e.target.value })} placeholder="Create a strong password" />
                </div>
                {/* Live password rules */}
                <ul className="grid gap-1.5 pt-1">
                  {PASSWORD_RULES.map(rule => {
                    const met = rule.test(newStudent.password);
                    return (
                      <li key={rule.label} className={`flex items-center gap-2 text-xs transition-colors ${met ? "text-success" : "text-muted-foreground"}`}>
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 border ${met ? "bg-success border-success text-white" : "border-muted-foreground/40"}`}>
                          {met ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5 text-muted-foreground/50" />}
                        </span>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-muted-foreground pt-1">The student uses this email & password to sign in at the login page.</p>
              </div>
            </CardContent>
          </Card>

          {/* Assign Exams */}
          <Card className="glass-premium">
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <BookOpen className="h-4 w-4" />
                </span>
                Assign Exams
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-5">
              {/* Group selector */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">ThinkExam Group</Label>
                {groupsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading groups...
                  </div>
                ) : (
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select a group..." />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.length === 0 ? (
                        <SelectItem value="__none" disabled>No groups found</SelectItem>
                      ) : (
                        groups.map((g: any) => {
                          const name = g.groupName || g.name || g.group_name || String(g);
                          return <SelectItem key={name} value={name}>{name}</SelectItem>;
                        })
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Exam list */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <CheckSquare className="h-3.5 w-3.5" /> Select Exams
                </Label>
                {!selectedGroup ? (
                  <div className="rounded-xl border border-dashed border-border/60 py-8 text-center text-sm text-muted-foreground">
                    Select a group above to load its exams.
                  </div>
                ) : examsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading exams...
                  </div>
                ) : exams.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No exams found for this group.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {exams.map((exam: any) => {
                      const id = exam.testId || exam.id || exam.test_id;
                      const name = exam.testName || exam.name || exam.test_name || id;
                      const isSelected = selectedExams.some(e => e.testId === id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleExam(exam)}
                          className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl border transition-all text-sm ${isSelected ? "border-primary bg-primary/5 text-foreground" : "border-border/50 hover:border-primary/40 text-muted-foreground"}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                            {isSelected && <span className="text-white text-[10px] font-bold">✓</span>}
                          </div>
                          <BookOpen className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium truncate">{name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected exams chips */}
              {selectedExams.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Selected ({selectedExams.length})</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedExams.map(e => (
                      <Badge key={e.testId} variant="secondary" className="flex items-center gap-1 pr-1.5 text-xs">
                        {e.testName}
                        <button onClick={() => setSelectedExams(prev => prev.filter(x => x.testId !== e.testId))} className="ml-0.5 hover:text-destructive transition-colors">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sticky action bar */}
        <div className="sticky bottom-0 -mx-4 md:-mx-8 px-4 md:px-8 py-4 bg-background/80 backdrop-blur border-t border-border/50 flex items-center justify-end gap-3">
          <Button variant="outline" className="h-11 px-6" onClick={() => navigate("/growlearn")} disabled={creating}>
            Cancel
          </Button>
          <Button className="gradient-warm text-white border-0 shadow-md h-11 px-8" onClick={handleCreateStudent} disabled={creating}>
            {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : <><Plus className="mr-1 h-4 w-4" />Register Student</>}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RegisterStudent;
